import React, { useMemo, useState, useEffect } from 'react';
import { Menu, Layout } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CalendarOutlined,
  FolderOpenOutlined,
  TeamOutlined,
  ProjectOutlined,
  FileTextOutlined,
  ScheduleOutlined,
  ToolOutlined,
  DollarOutlined,
  CalculatorOutlined,
  BarChartOutlined,
  SettingOutlined,
  ApartmentOutlined,
  FileWordOutlined,
  MailOutlined,
  BellOutlined,
  AccountBookOutlined,
  ShopOutlined,
  DatabaseOutlined,
  UserOutlined,
  ApiOutlined,
  SafetyOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import useUIStore from '../../stores/uiStore';
import useTenantStore from '../../stores/tenantStore';
import { usePermissions } from '../../hooks/usePermissions';
import { APP_NAME } from '../../constants/appName';

const { Sider } = Layout;

const THEME = {
  sidebarBg: '#1B3A5C',
  sidebarActiveBg: '#3DA872',
  sidebarHover: '#1A6E8C',
  headerBg: '#FFFFFF',
  contentBg: '#F5F6FA',
  brandPrimary: '#1A6E8C',
  textOnDark: '#FFFFFF',
  textOnDarkMuted: '#B0C4D8',
} as const;

type MenuItem = Required<MenuProps>['items'][number];

function item(
  label: string,
  key: string,
  icon: React.ReactNode,
  children?: MenuItem[],
): MenuItem {
  return { key, icon, children, label } as MenuItem;
}

// Maps each sub-group key to the path prefixes that should open it
const GROUP_PATH_MAP: Record<string, string[]> = {
  'sub-projets': ['/clients', '/projets', '/finance/offres', '/participants'],
  'sub-horaire': ['/horaire', '/personnel', '/admin/calendrier'],
  'sub-compta': ['/finance/factures', '/budget', '/tableau-de-bord', '/rapports', '/finance/transferts', '/finance/liaison-dofin'],
  'sub-params': ['/admin/gabarits', '/admin/gabarits-courriel', '/admin/regles-alertes', '/admin/comptabilite', '/services', '/fournisseurs', '/admin/utilisateurs', '/admin/permissions', '/admin/parametres', '/admin/import', '/ressources'],
};

function resolveOpenKeys(pathname: string): string[] {
  const open: string[] = [];
  for (const [groupKey, prefixes] of Object.entries(GROUP_PATH_MAP)) {
    if (prefixes.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'))) {
      open.push(groupKey);
    }
  }
  return open;
}

export default function AppSidebar(): React.ReactElement {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const tenant = useTenantStore((s) => s.tenant);
  const perms = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const has = (module: string, action: string): boolean =>
    Boolean(perms[module]?.[action]);

  const menuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [];

    // 1. Tableau Gantt — toujours visible
    items.push(item('Tableau Gantt', '/', <CalendarOutlined />));

    // 2. Groupe Projets — group guard relaxed to the union of its children's guards
    if (has('projects', 'read') || has('crm', 'read') || has('opportunities', 'read') || has('participants', 'read')) {
      const projetsChildren: MenuItem[] = [];

      if (has('crm', 'read')) {
        projetsChildren.push(item('Clients', '/clients', <TeamOutlined />));
      }
      if (has('projects', 'read')) {
        projetsChildren.push(item('Projets', '/projets', <ProjectOutlined />));
      }
      if (has('participants', 'read')) {
        projetsChildren.push(item('Participants', '/participants', <UserOutlined />));
      }
      if (has('finance', 'read')) {
        projetsChildren.push(item('Offres de service', '/finance/offres', <FileTextOutlined />));
      }

      if (projetsChildren.length > 0) {
        items.push(item('Projets', 'sub-projets', <FolderOpenOutlined />, projetsChildren));
      }
    }

    // 3. Groupe Horaire — group guard relaxed to the union of its children's guards
    // (each child route is protected by its own module in router/index.tsx, so the
    // sidebar guard must match: Réservations/Ressources → resources.read,
    // Calendrier scolaire → admin.manage_settings, Horaire/Personnel → schedule.read)
    if (has('schedule', 'read') || has('admin', 'manage_settings')) {
      const horaireChildren: MenuItem[] = [];

      if (has('schedule', 'read')) {
        horaireChildren.push(item('Horaire de projet', '/horaire', <ScheduleOutlined />));
        horaireChildren.push(item('Personnel', '/personnel', <ApartmentOutlined />));
      }
      if (has('admin', 'manage_settings')) {
        horaireChildren.push(item('Calendrier scolaire', '/admin/calendrier', <CalendarOutlined />));
      }

      if (horaireChildren.length > 0) {
        items.push(item('Horaire', 'sub-horaire', <ScheduleOutlined />, horaireChildren));
      }
    }

    // 4. Groupe Comptabilité — group guard relaxed to the union of its children's guards
    if (
      has('finance', 'read') ||
      has('budget', 'read') ||
      has('dashboard', 'read') ||
      has('reports', 'read') ||
      has('reports', 'build') ||
      has('finance', 'write')
    ) {
      const comptaChildren: MenuItem[] = [];

      if (has('finance', 'read')) {
        comptaChildren.push(item('Factures', '/finance/factures', <DollarOutlined />));
      }
      if (has('budget', 'read')) {
        comptaChildren.push(item('Budget Annuel', '/budget', <CalculatorOutlined />));
      }
      if (has('dashboard', 'read')) {
        comptaChildren.push(item('Tableau de bord', '/tableau-de-bord', <BarChartOutlined />));
      }
      if (has('reports', 'read') || has('reports', 'build')) {
        comptaChildren.push(item('Rapports', '/rapports', <FileTextOutlined />));
      }
      // Entrée menu retirée ; route /finance/transferts conservée.
      if (has('finance', 'write')) {
        comptaChildren.push(item('Liaison DOFIN', '/finance/liaison-dofin', <ApiOutlined />));
      }

      if (comptaChildren.length > 0) {
        items.push(item('Comptabilité', 'sub-compta', <DollarOutlined />, comptaChildren));
      }
    }

    // 5. Groupe Paramètres — group guard relaxed to include crm.read (needed for Fournisseurs)
    const hasAdminAccess =
      has('admin', 'manage_users') ||
      has('admin', 'manage_permissions') ||
      has('admin', 'manage_settings') ||
      has('documents', 'manage_templates') ||
      has('crm', 'read') ||
      has('resources', 'read');

    if (hasAdminAccess) {
      const paramsChildren: MenuItem[] = [];

      if (has('documents', 'manage_templates')) {
        paramsChildren.push(item('Gabarits documents', '/admin/gabarits', <FileWordOutlined />));
        paramsChildren.push(item('Gabarits courriel', '/admin/gabarits-courriel', <MailOutlined />));
      }
      if (has('admin', 'manage_settings')) {
        paramsChildren.push(item("Règles d'alertes", '/admin/regles-alertes', <BellOutlined />));
        paramsChildren.push(item('Codes Budgétaires', '/admin/comptabilite', <AccountBookOutlined />));
        paramsChildren.push(item('Services', '/services', <ShopOutlined />));
      }
      if (has('crm', 'read') || has('admin', 'manage_settings')) {
        paramsChildren.push(item('Fournisseurs', '/fournisseurs', <DatabaseOutlined />));
      }
      if (has('admin', 'manage_users')) {
        paramsChildren.push(item('Utilisateurs', '/admin/utilisateurs', <UserOutlined />));
      }
      if (has('admin', 'manage_permissions')) {
        paramsChildren.push(item('Permissions', '/admin/permissions', <SafetyOutlined />));
      }
      if (has('resources', 'read')) {
        paramsChildren.push(item('Ressources', '/ressources', <ToolOutlined />));
      }
      if (has('admin', 'manage_settings')) {
        paramsChildren.push(item('Paramètres généraux', '/admin/parametres', <SettingOutlined />));
        paramsChildren.push(item('Import CSV', '/admin/import', <ImportOutlined />));
      }

      if (paramsChildren.length > 0) {
        items.push(item('Paramètres', 'sub-params', <SettingOutlined />, paramsChildren));
      }
    }

    // 6. Archives — standalone, non-collapsible
    if (has('archive', 'read')) {
      items.push(item('Archives', '/archives', <FolderOpenOutlined />));
    }

    return items;
  }, [perms]);

  const selectedKey = location.pathname;

  const ALL_GROUPS = ['sub-projets', 'sub-horaire', 'sub-compta', 'sub-params'];

  // Open all groups by default; allow user to collapse/expand manually.
  // Auto-open the group that matches the current URL when navigating.
  const [openKeys, setOpenKeys] = useState<string[]>(
    collapsed ? [] : ALL_GROUPS,
  );

  useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
    } else {
      // Ensure the group for the current route is open, keep others as-is.
      const activeGroups = resolveOpenKeys(location.pathname);
      setOpenKeys((prev) => {
        const merged = new Set([...prev, ...activeGroups]);
        return Array.from(merged);
      });
    }
  }, [location.pathname, collapsed]);


  return (
    <Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={250}
      collapsedWidth={80}
      style={{
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        background: THEME.sidebarBg,
        overflow: 'auto',
      }}
    >
      {/* Branding */}
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          padding: '0 16px',
          overflow: 'hidden',
        }}
      >
        {tenant.logo_url ? (
          <img
            src={tenant.logo_url}
            alt={tenant.company_name}
            style={{
              maxHeight: 44,
              maxWidth: collapsed ? 48 : 200,
              objectFit: 'contain',
            }}
          />
        ) : (
          <span
            style={{
              color: THEME.textOnDark,
              fontWeight: 700,
              fontSize: collapsed ? 13 : 15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {collapsed ? 'PG' : APP_NAME}
          </span>
        )}
      </div>

      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={(keys) => setOpenKeys(keys as string[])}
        items={menuItems}
        onClick={({ key }) => navigate(key)}
        style={{
          borderRight: 0,
          marginTop: 8,
          background: THEME.sidebarBg,
          color: THEME.textOnDark,
        }}
        theme="dark"
      />

      <style>{`
        .ant-menu-dark {
          background: ${THEME.sidebarBg} !important;
        }
        .ant-menu-dark .ant-menu-sub {
          background: ${THEME.sidebarBg} !important;
        }
        .ant-menu-dark .ant-menu-item-selected {
          background-color: ${THEME.sidebarActiveBg} !important;
          color: ${THEME.textOnDark} !important;
        }
        .ant-menu-dark .ant-menu-item:hover,
        .ant-menu-dark .ant-menu-submenu-title:hover {
          background-color: ${THEME.sidebarHover} !important;
          color: ${THEME.textOnDark} !important;
        }
        .ant-menu-dark .ant-menu-item,
        .ant-menu-dark .ant-menu-submenu-title {
          color: ${THEME.textOnDarkMuted} !important;
        }
        .ant-menu-dark .ant-menu-item-selected,
        .ant-menu-dark .ant-menu-item-selected .anticon,
        .ant-menu-dark .ant-menu-item-selected span {
          color: ${THEME.textOnDark} !important;
        }
      `}</style>
    </Sider>
  );
}
